/**
 * §6.5-7 추론(유령) 자막 접기 설정 — 이 브라우저의 취향(localStorage,
 * use-source-veil.ts 와 같은 규율). 서버 왕복 없음.
 */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'omnitrans:collapse-inferred'

/**
 * 저장값 해석 — 저장된 값이 없으면 기본값은 **접힘**이다.
 * 지금은 사용자가 유령(추론된) 자막을 전부 펼쳐서 보고 있는 상태이므로,
 * 접기는 되돌릴 수 있는 판단(펼치면 다시 보인다)이라 기본값을 "접힘"으로
 * 둔다.
 */
function parseCollapse(raw: unknown): boolean {
  return raw === null ? true : raw === '1'
}

export function useCollapseInferred(): {
  collapsed: boolean
  setCollapsed: (v: boolean) => void
  toggle: () => void
} {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return true
    try {
      return parseCollapse(localStorage.getItem(STORAGE_KEY))
    } catch {
      return true
    }
  })

  /** 다른 탭의 토글을 따라간다(use-source-veil.ts 와 같은 규율) — storage
   *  이벤트는 **다른 문서**의 쓰기에만 오므로 자기 토글과 겹치지 않는다. */
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === STORAGE_KEY) setCollapsedState(parseCollapse(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
    } catch {
      // 저장 실패는 무시한다 — 접기 설정은 이번 세션만 유지된다.
    }
  }, [])

  const toggle = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, setCollapsed, toggle }
}
